import { Car, MapPinned, Route, Wrench } from "lucide-react";
import { SectionLabel } from "@/components/FormField";
import { formatUSD } from "@/lib/api";

const ICONS = [Car, Wrench, Route, MapPinned];

export const Services = ({ profile }) => (
  <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
    <SectionLabel>what we do</SectionLabel>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide mt-3 max-w-2xl">Towing capabilities</h2>
    {profile.about && <p className="mt-4 text-slate-400 max-w-2xl text-sm sm:text-base" data-testid="about-text">{profile.about}</p>}
    <div data-testid="services-section-grid" className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800 border border-slate-800">
      {profile.services.map((s, i) => {
        const Icon = ICONS[i % ICONS.length];
        return (
          <article key={i} data-testid={`service-card-${i}`} className="group bg-[#070B16] p-6 hover:bg-[#0A1224] transition-colors duration-300">
            <div className="flex items-center justify-between">
              <Icon className="w-6 h-6 text-cyan-400 group-hover:text-[#FF007F] transition-colors" />
              <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
            </div>
            <h3 className="font-display text-sm uppercase tracking-widest mt-8 text-slate-100">{s.title}</h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.description}</p>
          </article>
        );
      })}
    </div>
  </section>
);

const TagList = ({ items, color, testId }) => (
  <ul data-testid={testId} className="flex flex-wrap gap-2 mt-5">
    {items.map((a) => (
      <li key={a} className={`font-mono text-xs uppercase tracking-wider px-3 py-2 border ${color}`}>{a}</li>
    ))}
  </ul>
);

export const Coverage = ({ profile }) => (
  <section id="coverage" className="border-y border-slate-800 bg-[#060A14] cyber-grid">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12">
      <div>
        <SectionLabel>service areas</SectionLabel>
        <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide mt-3">Coverage zone</h2>
        <TagList items={profile.service_areas} testId="service-area-list" color="border-cyan-500/40 text-cyan-300 bg-cyan-500/5" />
      </div>
      <div>
        <SectionLabel>transport options</SectionLabel>
        <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide mt-3">Fleet modes</h2>
        <TagList items={profile.transport_options} testId="transport-options-list" color="border-[#FF007F]/40 text-[#FF7AB8] bg-[#FF007F]/5" />
      </div>
    </div>
  </section>
);

export const PricingStrip = ({ profile }) => (
  <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
    <div className="border glow-green bg-[#050A10] p-8 sm:p-12 grid md:grid-cols-[1fr_auto] gap-8 items-center">
      <div>
        <SectionLabel>pricing engine</SectionLabel>
        <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide mt-3">Transparent rates</h2>
        <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-xl">
          Every job includes a fixed {formatUSD(profile.base_charge)} service charge plus {formatUSD(profile.per_mile_rate)} per mile between pickup and drop-off. Sign in to get your exact estimate before you submit.
        </p>
      </div>
      <div className="font-mono text-right" data-testid="pricing-strip-formula">
        <p className="text-4xl sm:text-5xl text-[#00FF66] text-glow-green">{formatUSD(profile.base_charge)}</p>
        <p className="text-slate-400 text-sm mt-1">+ {formatUSD(profile.per_mile_rate)} / mile</p>
      </div>
    </div>
  </section>
);
